import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Directive, ElementRef, EventEmitter, forwardRef, Host, HostBinding, HostListener, Inject, Input, Optional, Output, ViewContainerRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MAT_LEGACY_FORM_FIELD as MAT_FORM_FIELD, MatLegacyFormField as MatFormField } from '@angular/material/legacy-form-field';
import { TranslocoService } from '@ngneat/transloco';
import { Observable } from 'rxjs';
import { first, takeUntil, tap } from 'rxjs/operators';
import { I18N_CONTEXT } from '../../common';
import { LyricsEditorComponent } from '../editor/lyrics-editor.component';

@Directive({
  selector: 'input[hdisLyricsEditor], textarea[hdisLyricsEditor]',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => LyricsEditorTriggerDirective), multi: true },
  ],
  standalone: true,
})
export class LyricsEditorTriggerDirective implements ControlValueAccessor {
  @HostBinding('attr.readonly') readonly = true;

  @HostBinding('class.ellipsis') ellipsis = true;

  isActive: boolean;

  isDisabled: boolean;

  propagateFn: Function;

  currentValue: any;

  release$: Observable<any>;

  editor: LyricsEditorComponent;

  @Input('hdisLyricsEditor') fieldName: string;

  @Input('hdisLyricsEditorTarget') target: ViewContainerRef;

  @Input('readonly')
  get readOnly(): boolean {
    return this._readOnly;
  }

  set readOnly(value) {
    this._readOnly = coerceBooleanProperty(value);
  }

  _readOnly = false;

  @Output() opened = new EventEmitter<void>();

  @Output() closed = new EventEmitter<void>();

  constructor(
    private _element: ElementRef<HTMLInputElement>,
    private i18n: TranslocoService,
    @Optional() @Inject(I18N_CONTEXT) public i18nContext: string,
    @Optional() @Inject(MAT_FORM_FIELD) @Host() private _formField: MatFormField,
  ) { }

  writeValue(value: any): void {
    this.currentValue = value;
    if (this.isActive) this.editor.setLyrics(this.currentValue);
    // If it's used within a `MatFormField`, we should set it through the property so it can go
    // through change detection.
    if (this._formField) {
      this._formField._control.value = this.currentValue;
    } else {
      this._element.nativeElement.value = this.currentValue;
    }
  }

  registerOnChange(fn: any): void {
    this.propagateFn = fn;
  }

  registerOnTouched(fn: any): void {
    console.debug('Method registerOnTouched not implemented for lyricsEditorDirective.');
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  @HostListener('focusin', ['$event'])
  onFocusIn(evt: FocusEvent) {
    if (!this.target) return;

    // empty the editor container from previous components, if any
    this.target.clear();

    // Create enum picker dynamically and append it to the editor container
    const componentRef = this.target.createComponent(LyricsEditorComponent);
    // save a reference to the enum picker for future use
    this.editor = componentRef.instance;

    this.editor.opened
      .pipe(first())
      .subscribe(() => {
        console.debug(`subscribe to lyrics editor events for ${this.fieldName}`);
        this.isActive = true;
        this.opened.emit();
        this.release$ = this.editor.closed
          .pipe(tap(() => {
            console.debug(`release subscriptions for ${this.fieldName}`);
            this.isActive = false;
            this.closed.emit();
          }));
        this.editor.lyricsChange
          .pipe(takeUntil(this.release$)).subscribe((newValue) => {
            console.debug(`updated value for ${this.fieldName}`, newValue);
            this.propagateFn(newValue || null);
            // this.writeValue(newValue || null)
          });
      });

    this.editor.title = this.i18n.translate<string>(`${this.i18nContext}${this.fieldName}`);
    this.editor.isReadonly = this.readOnly;
    this.editor.isDisabled = this.isDisabled;
    this.editor.open(this.fieldName, this.currentValue);
  }
}
